/**
 * Runtime restaurant identity for storefront entry modes.
 *
 * ALIAS (PLATFORM_SLUG): /r/{slug} — RestaurantId from bootstrap only. Fail closed.
 * CUSTOM_HOSTNAME: FQDN host (2+ labels, not localhost/IP) — RestaurantId from
 *   host-only bootstrap. Fail closed.
 * LEGACY LOCALHOST: loopback / IP / single-label hosts on `/` (not /r/{slug}).
 *   Enabled only when BOTH are set at build time:
 *     REACT_APP_ENABLE_LEGACY_LOCALHOST=true
 *     REACT_APP_RESTAURANT_ID=<uuid>
 *   Otherwise the visit is unavailable — never invent a tenant.
 *
 * PLATFORM: a configured platform host (rasoix.app). Not a storefront at all — no
 *   restaurant is resolved, no bootstrap is called, and RestaurantId stays empty.
 *
 * There is no baked restaurant UUID. Alias/host bootstrap never reads
 * REACT_APP_RESTAURANT_ID.
 */

export type StorefrontEntryMode =
  | "unset"
  | "legacy"
  | "alias"
  | "custom_hostname"
  | "platform";

export type PublicTenantStatus =
  | "idle"
  | "loading"
  | "resolved"
  | "unavailable"
  | "error";

/** UUID-shaped restaurant id (accepts the 8-4-4-4-12 hex form used by the platform). */
const RESTAURANT_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

let entryMode: StorefrontEntryMode = "unset";
let resolvedRestaurantId: string | null = null;
let resolvedSlug: string | null = null;

function isValidRestaurantId(value: string | null | undefined): value is string {
  const id = value?.trim() ?? "";
  return RESTAURANT_ID_PATTERN.test(id);
}

/**
 * True only when local-dev legacy mode is explicitly opted in with a valid id.
 */
export function isLegacyLocalhostOptInEnabled(): boolean {
  return (
    process.env.REACT_APP_ENABLE_LEGACY_LOCALHOST?.trim() === "true" &&
    isValidRestaurantId(process.env.REACT_APP_RESTAURANT_ID)
  );
}

/**
 * Restaurant id for opted-in localhost legacy mode. Empty when opt-in is absent
 * or the id is missing/invalid — callers must fail closed.
 */
export function readLegacyLocalhostRestaurantId(): string {
  if (!isLegacyLocalhostOptInEnabled()) return "";
  return process.env.REACT_APP_RESTAURANT_ID!.trim();
}

/** Path match for alias entry: /r/{slug} or /r/{slug}/... */
export function matchAliasSlugFromPath(pathname: string): string | null {
  const m = pathname.match(/^\/r\/([^/]+)(?:\/|$)/i);
  if (!m?.[1]) return null;
  try {
    return decodeURIComponent(m[1]).trim();
  } catch {
    return m[1].trim();
  }
}

export function isAliasPath(pathname: string): boolean {
  return matchAliasSlugFromPath(pathname) != null;
}

/**
 * Candidate CUSTOM_HOSTNAME browser host (URL shape only — server remains tenant authority).
 * Rejects localhost / loopback / IP literals so those hosts are never treated as tenants
 * unless /r/{slug} is used or explicit localhost legacy opt-in is enabled.
 */
export function isCustomHostnameCandidate(hostname: string): boolean {
  const host = normalizeBrowserHostname(hostname);

  if (!host || host === "localhost" || host === "127.0.0.1" || host === "::1") {
    return false;
  }

  // IPv4
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return false;
  // IPv6-ish
  if (host.includes(":")) return false;

  const labels = host.split(".").filter(Boolean);
  return labels.length >= 2;
}

/**
 * Normalizes a browser hostname for comparison: lowercase, port stripped, IPv6
 * brackets removed. Shared by the platform-host and custom-hostname checks so the two
 * cannot disagree about what host they are looking at.
 */
function normalizeBrowserHostname(hostname: string): string {
  const raw = (hostname ?? "").trim().toLowerCase();
  if (!raw) return "";
  return raw.includes(":") && !raw.startsWith("[")
    ? raw.slice(0, raw.lastIndexOf(":"))
    : raw.replace(/^\[|\]$/g, "");
}

/**
 * Hosts that serve the RasoiX platform (discovery) rather than a restaurant storefront.
 *
 * Build-time and explicit: `REACT_APP_PLATFORM_HOSTS` as a comma-separated list, e.g.
 * `rasoix.app,www.rasoix.app`. Unset means no platform hosts, which is exactly the
 * behaviour that shipped before platform mode existed — every FQDN keeps being treated
 * as a candidate restaurant custom hostname.
 *
 * A list rather than a suffix rule: `*.rasoix.app` IS the tenant namespace, so any
 * suffix match would swallow every restaurant subdomain.
 */
export function readPlatformHosts(): string[] {
  const raw = process.env.REACT_APP_PLATFORM_HOSTS ?? "";
  return raw
    .split(",")
    .map((h) => normalizeBrowserHostname(h))
    .filter((h) => h.length > 0);
}

/**
 * True when `hostname` is a configured platform host.
 *
 * Callers MUST check this before {@link isCustomHostnameCandidate}: a platform host is
 * a well-formed FQDN and would otherwise be taken for a restaurant custom hostname.
 * This is a client-side routing decision only — the server independently refuses to
 * resolve platform hosts as tenants via HostTrust ReservedHosts.
 */
export function isPlatformHost(hostname: string): boolean {
  const host = normalizeBrowserHostname(hostname);
  if (!host) return false;
  return readPlatformHosts().includes(host);
}

/**
 * Activates platform entry. No restaurant is resolved and none may be: RestaurantId
 * stays empty for the lifetime of the navigation.
 */
export function beginPlatformEntry(): void {
  entryMode = "platform";
  resolvedRestaurantId = null;
  resolvedSlug = null;
}

export function isPlatformEntryMode(): boolean {
  return entryMode === "platform";
}

export function getStorefrontEntryMode(): StorefrontEntryMode {
  return entryMode;
}

export function getResolvedSlug(): string | null {
  return resolvedSlug;
}

/**
 * Activates alias mode for the current navigation. Clears any prior restaurant id.
 * Does NOT read REACT_APP_RESTAURANT_ID.
 */
export function beginAliasEntry(slug: string): void {
  entryMode = "alias";
  resolvedRestaurantId = null;
  resolvedSlug = slug.trim().toLowerCase();
}

/**
 * Activates CUSTOM_HOSTNAME entry. Clears restaurant id until host-only bootstrap succeeds.
 * Does NOT read REACT_APP_RESTAURANT_ID. Does not invent a platform slug from the hostname.
 */
export function beginCustomHostnameEntry(): void {
  entryMode = "custom_hostname";
  resolvedRestaurantId = null;
  resolvedSlug = null;
}

/**
 * Activates opted-in localhost legacy mode. Restaurant id is empty when opt-in is
 * missing — callers must treat that as unavailable, not resolved.
 */
export function beginLegacyEntry(): void {
  entryMode = "legacy";
  resolvedSlug = null;
  const id = readLegacyLocalhostRestaurantId();
  resolvedRestaurantId = id || null;
}

/**
 * Sets RestaurantId after successful alias bootstrap only.
 */
export function setAliasResolvedRestaurantId(restaurantId: string): void {
  if (entryMode !== "alias") {
    throw new Error("setAliasResolvedRestaurantId requires alias entry mode");
  }
  const id = restaurantId.trim();
  if (!id) {
    throw new Error("Alias-resolved restaurant id is required");
  }
  resolvedRestaurantId = id;
}

/**
 * Sets RestaurantId after successful CUSTOM_HOSTNAME bootstrap only.
 */
export function setCustomHostnameResolvedRestaurantId(restaurantId: string): void {
  if (entryMode !== "custom_hostname") {
    throw new Error("setCustomHostnameResolvedRestaurantId requires custom_hostname entry mode");
  }
  const id = restaurantId.trim();
  if (!id) {
    throw new Error("Custom-hostname-resolved restaurant id is required");
  }
  resolvedRestaurantId = id;
}

export function clearAliasResolvedRestaurantId(): void {
  if (entryMode === "alias" || entryMode === "custom_hostname") {
    resolvedRestaurantId = null;
  }
}

/**
 * Authoritative runtime RestaurantId for API compatibility bridge.
 * Alias / custom_hostname: empty until bootstrap succeeds.
 * Legacy: opted-in env id, otherwise empty.
 * Platform: always empty — the platform host has no tenant.
 * Unset (before providers): empty — never invent a tenant.
 */
export function getRestaurantId(): string {
  if (entryMode === "alias" || entryMode === "custom_hostname" || entryMode === "legacy") {
    return resolvedRestaurantId?.trim() ?? "";
  }
  return "";
}

export function isAliasEntryMode(): boolean {
  return entryMode === "alias";
}

export function isCustomHostnameEntryMode(): boolean {
  return entryMode === "custom_hostname";
}

/** True when public storefront URLs should be slug-less (TrustedHostname tenancy). */
export function usesHostOnlyPublicPaths(): boolean {
  return entryMode === "custom_hostname";
}

/** Test helper — resets module state between Jest cases. */
export function resetRestaurantIdRuntimeForTests(): void {
  entryMode = "unset";
  resolvedRestaurantId = null;
  resolvedSlug = null;
}
